import axios from 'axios';
import toast from 'react-hot-toast';
import { isAccountPage } from '../utils/accountPages';

// API base URL configuration:
// 1. In production, always use REACT_APP_API_URL
// 2. In development, use relative URL so webpack proxy can work
// 3. From another device (e.g. phone), use that host for local testing
const getApiBaseUrl = () => {
  // Always use environment variable if set (production builds will have this)
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;

  // Development fallback: detect if opened from another device for local testing
  const host = typeof window !== 'undefined' && window.location?.hostname;
  const isLocalhost = !host || host === 'localhost' || host === '127.0.0.1';
  const isPrivateIP = host && /^(192\.168\.|10\.|172\.(1[6-9]|2\d|3[01])\.)/.test(host);

  if (!isLocalhost && isPrivateIP) {
    return `http://${host}:3000/v1`;
  }
  // Use relative URL in development so webpack dev server proxy works
  return '/v1';
};

const API_BASE_URL = getApiBaseUrl();

const API = axios.create({
  baseURL: API_BASE_URL,
});

// ── Refresh-token machinery ──────────────────────────────────────────────
// On a 401 we try to silently mint a fresh access token via /auth/refresh
// and replay the original request, so a transient auth glitch (instance
// rotation during deploy, brief network blip, edge-case clock skew) never
// drops the customer back to the login page. Concurrent 401s share a single
// in-flight refresh so we don't fire N parallel refreshes.
let _isRefreshing = false;
let _refreshQueue = [];

function _onRefreshDone(newToken) {
  _refreshQueue.forEach((cb) => cb(newToken));
  _refreshQueue = [];
}

function _clearAndRedirect(error) {
  localStorage.removeItem('authToken');
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('customer');
  localStorage.removeItem('customerId');
  // Tell the app the session is gone so the UI flips to logged-out immediately
  // (AuthContext listens). Otherwise the cached name/avatar keep showing and the
  // user looks "logged in" while every authed call 401s.
  if (typeof window !== 'undefined') {
    try { window.dispatchEvent(new CustomEvent('stylon:auth-cleared')); } catch (_) { /* noop */ }
  }
  const pathname = (typeof window !== 'undefined' && window.location?.pathname) || '';
  const search = (typeof window !== 'undefined' && window.location?.search) || '';
  // Only a page that exists for a signed-in customer (their profile, orders,
  // bookings, stays — utils/accountPages.js) sends the visitor to /login.
  // Every other page is public — a shop, a product, the checkout, a
  // confirmation, a stay, a course, the booking flow, on the platform or on a
  // business's own domain — so there the stale token is cleared quietly (above)
  // and the page's own login popup asks when the shopper acts. Public is the
  // default: a page nobody listed can never throw a shopper out of a purchase.
  if (isAccountPage(pathname)) {
    const returnUrl = pathname + search;
    const redirectParam = returnUrl ? '?redirect=' + encodeURIComponent(returnUrl) : '';
    toast.error('Please log in to continue');
    setTimeout(() => {
      if (typeof window !== 'undefined') window.location.href = '/login' + redirectParam;
    }, 100);
  }
  return Promise.reject(error);
}

// Add request interceptor: send auth token (customer store uses authToken / accessToken)
API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken') || localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: try a silent refresh on 401, then replay the
// request. Logs the customer out only when the refresh itself returns
// 401 (genuinely invalid refresh token); transient errors keep them
// signed in.
API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const status = error.response?.status;
    const url = typeof originalRequest.url === 'string' ? originalRequest.url : '';

    // Fire-and-forget: report any API error to Admin → Analytics → Errors.
    const reportError = () => {
      const msg = error.response?.data?.message || error.message || `HTTP ${status || 'error'}`;
      const syntheticError = new Error(msg);
      let requestUrl = url;
      if (requestUrl && originalRequest.baseURL && !requestUrl.startsWith('http')) {
        const base = originalRequest.baseURL.replace(/\/+$/, '');
        const path = requestUrl.replace(/^\/+/, '');
        requestUrl = base + (path ? '/' + path : '');
      }
      import('../utils/errorReporting').then(({ reportClientError }) => {
        reportClientError(syntheticError, {
          type: 'api_error',
          statusCode: status,
          requestUrl: requestUrl || undefined,
        });
      }).catch(() => {});
    };

    if (status !== 401) {
      reportError();
      return Promise.reject(error);
    }

    // 401 path. Only replay/refresh once per original request.
    if (originalRequest._retry) {
      reportError();
      return Promise.reject(error);
    }

    // Don't try to refresh on the refresh call itself or on the upload
    // endpoints (legacy parity with portal — uploads have their own
    // failure UX).
    if (originalRequest._isRefreshAttempt) {
      reportError();
      return _clearAndRedirect(error);
    }
    if (/\/auth\/refresh$/i.test(url) || /\/upload\//i.test(url)) {
      reportError();
      return _clearAndRedirect(error);
    }

    // Non-critical reads — never log the user out for these.
    const isNonCritical = /\/auth\/me\/locale$/i.test(url) || /\/web-chat\/config$/i.test(url);
    if (isNonCritical) {
      reportError();
      return Promise.reject(error);
    }

    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      // No refresh token (legacy session pre-dating this fix) → fall back
      // to the original "clear-and-redirect" behaviour. Eventually all
      // active sessions will have a refresh token and this branch becomes
      // dead code.
      reportError();
      return _clearAndRedirect(error);
    }

    // Concurrent 401: if a refresh is already in flight, queue this
    // request and replay it once the new token is ready.
    if (_isRefreshing) {
      return new Promise((resolve, reject) => {
        _refreshQueue.push((newToken) => {
          if (!newToken) {
            reject(error);
            return;
          }
          originalRequest.headers = { ...originalRequest.headers, Authorization: `Bearer ${newToken}` };
          originalRequest._retry = true;
          resolve(API(originalRequest));
        });
      });
    }

    _isRefreshing = true;
    try {
      const res = await axios.post(
        `${API_BASE_URL}/auth/refresh`,
        { refreshToken },
        { _isRefreshAttempt: true },
      );
      const newAccess = res.data?.accessToken;
      const newRefresh = res.data?.refreshToken;
      if (!newAccess) throw new Error('refresh: no access token in response');
      localStorage.setItem('authToken', newAccess);
      localStorage.setItem('accessToken', newAccess);
      if (newRefresh) localStorage.setItem('refreshToken', newRefresh);
      _isRefreshing = false;
      _onRefreshDone(newAccess);
      // Retry the original request with the new token.
      originalRequest.headers = { ...originalRequest.headers, Authorization: `Bearer ${newAccess}` };
      originalRequest._retry = true;
      return API(originalRequest);
    } catch (refreshErr) {
      _isRefreshing = false;
      _onRefreshDone(null);
      // Distinguish "refresh token actually rejected" (clear session) from
      // "transient error / network blip / 5xx" (keep user signed in,
      // surface the original error so the caller can retry).
      const refreshStatus = refreshErr?.response?.status;
      if (refreshStatus === 401) {
        reportError();
        return _clearAndRedirect(error);
      }
      // Transient — leave session intact and let the caller handle it.
      reportError();
      return Promise.reject(error);
    }
  }
);

export default API;
export { API_BASE_URL };

/** Normalize stored upload paths to a browser-loadable URL (parity with portal). */
export const toUploadUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  if (url.startsWith('blob:') || url.startsWith('data:') || url.startsWith('http')) return url;
  if (url.startsWith('/uploads')) return url;
  try {
    const fallbackBase = (typeof window !== 'undefined' && window.location?.origin) || 'http://localhost';
    const u = new URL(url, fallbackBase + '/');
    const pathname = u.pathname.startsWith('/') ? u.pathname : `/${u.pathname}`;
    if (pathname.startsWith('/uploads')) return pathname;
  } catch {
    /* keep original */
  }
  return url;
};

/**
 * Returns the API base URL as a fully qualified origin (e.g.
 * "https://abc123.ngrok-free.dev/v1") suitable for handing to a third party
 * — used by BookingPaymentSheet to compute the Tranzila notify URL. Returns
 * the relative "/v1" string when the dev server proxy is in play, since we
 * can't make a private host public from inside the browser anyway.
 */
export function getApiBaseUrlForFetch() {
  return API_BASE_URL;
}
